import React, { useState, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { prefetchRoute } from '../../routes/prefetch';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import useFocusTrap from '../../hooks/useFocusTrap';

export default function ChainAdminSidebar() {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  useFocusTrap(open, panelRef, { onEscape: () => setOpen(false) });

  const navItems = [
    { to: '/chain', label: t('nav_dashboard') || 'Dashboard', icon: '🏢', end: true },
    { to: '/chain/branches', label: t('caNavBranches') || 'Şubeler', icon: '🏪' },
    { to: '/chain/reports', label: t('caNavReports') || 'Raporlar', icon: '📊' },
    { to: '/chain/analytics', label: t('caNavAnalytics') || 'Analitik', icon: '📈' },
    { to: '/chain/menu', label: t('caNavChainMenu') || 'Zincir Menü', icon: '📋' },
    { to: '/chain/staff', label: t('caNavStaff') || 'Personel', icon: '👥' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-expanded={open}
        aria-controls="chainadmin-sidebar"
        aria-label={t('openMenu') || 'Menüyü aç'}
        className="md:hidden fixed top-4 left-4 z-50 bg-emerald-600 text-white p-2 rounded-lg shadow-lg"
      ><span aria-hidden="true">☰</span></button>
      {open && <div className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setOpen(false)} aria-hidden="true" />}

      <aside
        id="chainadmin-sidebar"
        ref={panelRef}
        role={open ? 'dialog' : 'navigation'}
        aria-modal={open ? 'true' : undefined}
        aria-label={open ? undefined : (t('chainAdminNavLabel') || 'Zincir admin menü')}
        aria-labelledby={open ? 'chain-admin-nav-title' : undefined}
        className={`
        fixed md:relative top-0 left-0 h-full z-50
        w-64 flex flex-col
        bg-emerald-900 dark:bg-gray-900
        transform transition-transform duration-200
        ${open ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        {/* Logo */}
        <div className="p-5 border-b border-emerald-700 dark:border-gray-700 flex items-center justify-between">
          <div>
            <div id="chain-admin-nav-title" className="text-xl font-bold text-white"><span aria-hidden="true">🏢</span> {t('chainAdminTitle') || 'Zincir Admin'}</div>
            <div className="text-xs text-emerald-300 dark:text-gray-400">{t('chainAdminSubtitle') || 'Çoklu Şube Yönetimi'}</div>
          </div>
          <button onClick={() => setOpen(false)} aria-label={t('closeMenu') || 'Menüyü kapat'} className="md:hidden text-emerald-300 hover:text-white"><span aria-hidden="true">✕</span></button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={!!item.end}
              onClick={() => setOpen(false)}
              onMouseEnter={() => prefetchRoute(item.to)}
              onFocus={() => prefetchRoute(item.to)}
              onTouchStart={() => prefetchRoute(item.to)}
              aria-label={item.label}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-emerald-700 text-white'
                    : 'text-emerald-200 hover:bg-emerald-800 hover:text-white'
                }`
              }
            >
              <span aria-hidden="true">{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Bottom */}
        <div className="p-4 border-t border-emerald-700 dark:border-gray-700 space-y-2">
          <button
            onClick={toggleTheme}
            aria-label={isDark ? (t('theme_light') || 'Açık tema') : (t('theme_dark') || 'Koyu tema')}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-emerald-200 hover:bg-emerald-800 hover:text-white transition-colors"
          >
            <span aria-hidden="true">{isDark ? '☀️' : '🌙'}</span> {isDark ? (t('theme_light') || 'Açık tema') : (t('theme_dark') || 'Koyu tema')}
          </button>
          {user && (
            <div className="px-3 py-2">
              <div className="text-sm font-medium text-white">{user.name}</div>
              <div className="text-xs text-emerald-300">{t('chainAdminRoleLabel') || 'Zincir Yöneticisi'}</div>
            </div>
          )}
          <button
            onClick={handleLogout}
            aria-label={t('logout') || 'Çıkış yap'}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red-400 hover:bg-red-900/30 transition-colors"
          >
            <span aria-hidden="true">🚪</span> {t('logout') || 'Çıkış yap'}
          </button>
        </div>
      </aside>
    </>
  );
}
